/**
 * Basic network shape manifests for the Systems Mapping domain.
 *
 * These are the provider-neutral shapes (router, switch, firewall,
 * generic server, database cylinder, laptop, cloud) that a basic
 * Creately user reaches for when drawing a simple network diagram
 * without committing to a specific cloud provider.
 *
 * Each manifest registers its `semanticType` so the ontology can
 * match cross-vocabulary queries — e.g. `isA(node, 'datastore')` is
 * true for a basic `db_cylinder` AND an `aws_rds_postgres` AND an
 * Azure SQL manifest. This is what lets agent tools like "find all
 * public databases" work uniformly across shape libraries.
 *
 * Rendering intentionally uses simple rounded rectangles with the
 * label + a colored accent stripe. No external CDN icons, so these
 * shapes render immediately without the network flakiness the AWS
 * icons have.
 */

import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { z } from 'zod';
import { escapeXml, safeHex } from './svgSafe';

const basicNodeProps = z.object({
  name: z.string().default(''),
  resourceType: z.string().default('resource'),
  semanticType: z.string().default('artifact'),
  hostingProvider: z.string().default('basic'),
  fillColor: z.string().default('#FFFFFF'),
  strokeColor: z.string().default('#94a3b8'),
  textColor: z.string().default('#1e293b'),
  accentColor: z.string().default('#64748b'),
});

export type BasicNodeProps = z.infer<typeof basicNodeProps>;

function renderBasicNodeSvg(
  viewModel: { width?: number; height?: number },
  props: Record<string, unknown>,
): string {
  const w = viewModel.width ?? 160;
  const h = viewModel.height ?? 60;
  // Sanitize all user-controlled values that land in SVG attributes/text.
  // See `svgSafe.ts` for the threat model.
  const name = String(props.name || props.resourceType || 'Node');
  const rType = String(props.resourceType || '');
  const accent = safeHex(props.accentColor, '#64748b');
  const fill = safeHex(props.fillColor, '#FFFFFF');
  const stroke = safeHex(props.strokeColor, '#94a3b8');
  const textColor = safeHex(props.textColor, '#1e293b');
  const escapedName = escapeXml(name);
  const escapedRTypeUpper = escapeXml(rType.toUpperCase());

  return [
    `<rect x="0" y="0" width="${w}" height="${h}" rx="6" ry="6" fill="${fill}" stroke="${stroke}" stroke-width="1.5" />`,
    `<rect x="0" y="0" width="4" height="${h}" rx="2" ry="2" fill="${accent}" />`,
    `<text x="14" y="${Math.round(h / 2 - 4)}" font-size="12" font-weight="600" fill="${textColor}" font-family="Arial, sans-serif">${escapedName}</text>`,
    `<text x="14" y="${Math.round(h / 2 + 12)}" font-size="10" fill="#64748b" font-family="Arial, sans-serif">${escapedRTypeUpper}</text>`,
  ].join('');
}

function createBasicManifest(
  id: string,
  resourceType: string,
  semanticType: string,
  label: string,
  accent: string,
): ShapeManifest<BasicNodeProps> {
  return {
    id,
    version: '1.0.0',
    category: 'network',
    icon: 'box',
    propsSchema: basicNodeProps,
    defaultProps: () => ({
      name: label,
      resourceType,
      semanticType,
      hostingProvider: 'basic',
      fillColor: '#FFFFFF',
      strokeColor: '#94a3b8',
      textColor: '#1e293b',
      accentColor: accent,
    }),
    defaultDimensions: { width: 160, height: 60 },
    anchors: {
      snap: ['top', 'right', 'bottom', 'left', 'center'],
      perimeter: 'rect',
    },
    capabilities: {
      selectable: true,
      transformable: true,
      textEditable: true,
      connectable: true,
    },
    properties: [
      { key: 'name', label: 'Name', kind: 'string', required: true, ui: { group: 'identity', priority: 1 } },
      { key: 'resourceType', label: 'Type', kind: 'string', ui: { group: 'identity', priority: 2 } },
    ],
    acl: () => true,
    renderers: {
      svg: (args: { viewModel: unknown; props: unknown }) =>
        renderBasicNodeSvg(
          args.viewModel as { width?: number; height?: number },
          args.props as Record<string, unknown>,
        ),
    },
  };
}

// Semantic types below are LEAF types registered in `network/ontology.ts`
// (under `compute` / `network_edge` / `datastore` / `network_boundary` /
// `user_identity` etc.). Using leaf types here is what makes
// `isA(basic_server.semanticType, 'server')` and
// `isA(aws_ec2.semanticType, 'server')` BOTH return true — the cross-library
// ontology promise the PR is built on. Don't downgrade these to abstract
// bucket names like `compute` or `network`; that breaks the `isA` query.

// Compute: generic server / host
export const BasicServerManifest = createBasicManifest('basic_server', 'server', 'server', 'Server', '#3b82f6');
export const BasicLaptopManifest = createBasicManifest('basic_laptop', 'laptop', 'laptop', 'Laptop', '#06b6d4');
export const BasicWorkstationManifest = createBasicManifest('basic_workstation', 'workstation', 'workstation', 'Workstation', '#0ea5e9');

// Network edge devices
export const BasicRouterManifest = createBasicManifest('basic_router', 'router', 'router', 'Router', '#22c55e');
export const BasicSwitchManifest = createBasicManifest('basic_switch', 'switch', 'switch', 'Switch', '#16a34a');
export const BasicFirewallManifest = createBasicManifest('basic_firewall', 'firewall', 'firewall', 'Firewall', '#ef4444');
export const BasicLoadBalancerManifest = createBasicManifest('basic_load_balancer', 'load_balancer', 'load_balancer', 'Load Balancer', '#8b5cf6');

// Storage / data
export const BasicDatabaseManifest = createBasicManifest('basic_database', 'database', 'database', 'Database', '#2563eb');
export const BasicStorageManifest = createBasicManifest('basic_storage', 'storage', 'storage', 'Storage', '#0284c7');

// Abstract
export const BasicCloudManifest = createBasicManifest('basic_cloud', 'cloud', 'network_boundary', 'Cloud', '#64748b');
export const BasicInternetManifest = createBasicManifest('basic_internet', 'internet', 'internet', 'Internet', '#475569');
export const BasicUserManifest = createBasicManifest('basic_user', 'user', 'user_identity', 'User', '#f97316');

export const BasicShapeManifests = [
  BasicServerManifest,
  BasicLaptopManifest,
  BasicWorkstationManifest,
  BasicRouterManifest,
  BasicSwitchManifest,
  BasicFirewallManifest,
  BasicLoadBalancerManifest,
  BasicDatabaseManifest,
  BasicStorageManifest,
  BasicCloudManifest,
  BasicInternetManifest,
  BasicUserManifest,
];
